import React, { useEffect, useState } from "react";
import { UrduInput } from "../../../../Components/Shared/UrduInput";
import { UiModal } from "../../../../Components/Shared/UiModal";

const EMPTY_FORM = {
  name: "",
  name_ur: "",
  address: "",
  address_ur: "",
  phone: "",
  latitude: "",
  longitude: "",
};

function PickupPointModal({ isOpen, onClose, onSubmit, editPoint }) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setFormData(EMPTY_FORM);
      setErrors({});
      setIsSaving(false);
      return;
    }

    if (editPoint) {
      setFormData({
        name: editPoint.name || "",
        name_ur: editPoint.name_ur || "",
        address: editPoint.address || "",
        address_ur: editPoint.address_ur || "",
        phone: editPoint.phone || "",
        latitude: editPoint.latitude ?? "",
        longitude: editPoint.longitude ?? "",
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setErrors({});
  }, [isOpen, editPoint]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!formData.name.trim()) nextErrors.name = "Name is required";
    if (!formData.address.trim()) nextErrors.address = "Address is required";

    const lat = Number(formData.latitude);
    if (formData.latitude === "" || Number.isNaN(lat)) {
      nextErrors.latitude = "Latitude is required";
    } else if (lat < -90 || lat > 90) {
      nextErrors.latitude = "Latitude must be between -90 and 90";
    }

    const lng = Number(formData.longitude);
    if (formData.longitude === "" || Number.isNaN(lng)) {
      nextErrors.longitude = "Longitude is required";
    } else if (lng < -180 || lng > 180) {
      nextErrors.longitude = "Longitude must be between -180 and 180";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || isSaving) return;

    setIsSaving(true);
    try {
      await onSubmit({
        name: formData.name.trim(),
        name_ur: formData.name_ur.trim(),
        address: formData.address.trim(),
        address_ur: formData.address_ur.trim(),
        phone: formData.phone.trim(),
        latitude: Number(formData.latitude),
        longitude: Number(formData.longitude),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const isEditMode = Boolean(editPoint?.id);
  const fieldClass = (name) =>
    `ui-input-field ${errors[name] ? "border-brand-error" : ""}`;

  return (
    <UiModal
      isOpen={isOpen}
      onClose={onClose}
      eyebrow="Pickup Points"
      title={isEditMode ? "Edit Pickup Point" : "Add Pickup Point"}
      as="form"
      onSubmit={handleSubmit}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="ui-btn-cancel min-w-[108px]"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="ui-btn-save min-w-[108px]"
          >
            {isSaving ? "Saving..." : isEditMode ? "Update" : "Save"}
          </button>
        </>
      }
    >
      <section className="ui-modal-section">
        <h4 className="ui-modal-section-title">Location details</h4>
        <div className="space-y-4">
          <div>
            <label className="ui-form-label">
              Name (English) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Lahore"
              className={fieldClass("name")}
            />
            {errors.name ? <p className="ui-form-error">{errors.name}</p> : null}
            <UrduInput
              label="نام (اردو)"
              name="name_ur"
              value={formData.name_ur}
              onChange={handleChange}
            />
          </div>

          <div>
            <label className="ui-form-label">
              Address (English) <span className="text-red-500">*</span>
            </label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              rows={3}
              placeholder="12 B/1, Johar town, Lahore"
              className={`ui-textarea-field ${
                errors.address ? "border-brand-error" : ""
              }`}
            />
            {errors.address ? (
              <p className="ui-form-error">{errors.address}</p>
            ) : null}
            <UrduInput
              label="پتہ (اردو)"
              name="address_ur"
              value={formData.address_ur}
              onChange={handleChange}
              type="textarea"
              rows={3}
            />
          </div>

          <div>
            <label className="ui-form-label">Phone</label>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="03395111199"
              dir="ltr"
              className="ui-input-field"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="ui-form-label">
                Latitude <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="latitude"
                value={formData.latitude}
                onChange={handleChange}
                step="any"
                placeholder="31.46252348"
                dir="ltr"
                className={fieldClass("latitude")}
              />
              {errors.latitude ? (
                <p className="ui-form-error">{errors.latitude}</p>
              ) : null}
            </div>
            <div>
              <label className="ui-form-label">
                Longitude <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="longitude"
                value={formData.longitude}
                onChange={handleChange}
                step="any"
                placeholder="74.29995846"
                dir="ltr"
                className={fieldClass("longitude")}
              />
              {errors.longitude ? (
                <p className="ui-form-error">{errors.longitude}</p>
              ) : null}
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Paste coordinates from Google Maps. The website builds map links from
            these values.
          </p>
        </div>
      </section>
    </UiModal>
  );
}

export default PickupPointModal;
